import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/layout/AppLayout';
import { useAuth } from '../context/AuthContext';
import { getReportSummary, getReportTrends, getConsultantStats } from '../services/api';

/* ─────────────── Color maps ─────────────── */
const STATUS_COLORS = {
  open:        '#3b82f6', assigned:    '#8b5cf6',
  in_progress: '#f59e0b', pending:     '#6b7280',
  resolved:    '#10b981', closed:      '#374151',
};
const PRIORITY_COLORS = { critical: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#22c55e' };
const TYPE_COLORS = {
  incident: '#ef4444', service_request: '#3b82f6',
  change_request: '#8b5cf6', problem: '#f97316',
};
const CATEGORY_COLORS = ['#0284c7','#7c3aed','#059669','#e11d48','#d97706','#6b7280','#ec4899','#14b8a6'];

/* ─────────────── SVG Donut Chart ─────────────── */
function DonutChart({ data, colorMap, labelKey, countKey = 'count', size = 130 }) {
  const [hovered, setHovered] = useState(null);
  if (!data || data.length === 0)
    return <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>No data</p>;

  const total  = data.reduce((s, d) => s + (parseInt(d[countKey]) || 0), 0);
  const radius = 44;
  const cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * radius;
  let offset = 0;
  const slices = data.map((d, i) => {
    const count = parseInt(d[countKey]) || 0;
    const key   = d[labelKey] || 'Unknown';
    const color = colorMap[key] || CATEGORY_COLORS[i % CATEGORY_COLORS.length];
    const dash  = total > 0 ? (count / total) * circ : 0;
    const slice = { key, count, color, dash, offset, pct: total > 0 ? ((count / total) * 100).toFixed(1) : 0 };
    offset += dash;
    return slice;
  });

  const active = hovered ? slices.find(s => s.key === hovered) : null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {slices.map(s => (
            <circle
              key={s.key}
              cx={cx} cy={cy} r={radius}
              fill="none"
              stroke={s.color}
              strokeWidth={hovered === s.key ? 16 : 13}
              strokeDasharray={`${s.dash} ${circ - s.dash}`}
              strokeDashoffset={-s.offset}
              transform={`rotate(-90 ${cx} ${cy})`}
              style={{ cursor: 'pointer', transition: 'stroke-width 0.15s' }}
              onMouseEnter={() => setHovered(s.key)}
              onMouseLeave={() => setHovered(null)}
            />
          ))}
          {/* Center text */}
          <text x={cx} y={cy - 7} textAnchor="middle" fontSize="20" fontWeight="800"
            fill="var(--text-primary)" dominantBaseline="middle">
            {active ? active.count : total}
          </text>
          <text x={cx} y={cy + 12} textAnchor="middle" fontSize="9.5"
            fill="var(--text-muted)">
            {active ? active.pct + '%' : 'total'}
          </text>
        </svg>
      </div>
      {/* Legend */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
        {slices.map(s => (
          <div key={s.key}
            onMouseEnter={() => setHovered(s.key)}
            onMouseLeave={() => setHovered(null)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
              opacity: hovered && hovered !== s.key ? 0.45 : 1, transition: 'opacity 0.15s',
            }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: s.color, flexShrink: 0 }} />
            <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontWeight: 500, textTransform: 'capitalize' }}>
              {s.key.replace(/_/g, ' ')}
            </span>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-primary)', marginLeft: 'auto', paddingLeft: 6 }}>
              {s.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────── SVG Area / Line Chart ─────────────── */
function AreaChart({ trends }) {
  if (!trends || trends.length === 0)
    return <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No data available.</p>;

  const W = 800, H = 140;
  const pad = { l: 32, r: 16, t: 14, b: 28 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;
  const max = Math.max(...trends.map(d => d.count), 1);
  const n   = trends.length;

  const px = (i) => pad.l + (i / (n - 1)) * innerW;
  const py = (v) => pad.t + innerH - (v / max) * innerH;

  const pts   = trends.map((d, i) => [px(i), py(d.count)]);
  const line  = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(' ');
  const area  = `${line} L${pts[n-1][0]},${pad.t + innerH} L${pts[0][0]},${pad.t + innerH} Z`;

  /* Y grid lines */
  const gridYs = [0, 0.25, 0.5, 0.75, 1].map(f => ({
    y:    pad.t + innerH - f * innerH,
    val:  Math.round(f * max),
  }));

  /* X labels — show every 7th day for 30d, every 14th for 90d, every day for 7d */
  const step = n <= 7 ? 1 : n <= 30 ? 5 : 14;
  const xLabels = trends
    .map((d, i) => ({ i, day: d.day }))
    .filter((_, i) => i % step === 0 || i === n - 1);

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
        style={{ display: 'block', minWidth: 400 }}>
        <defs>
          <linearGradient id="aGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="var(--primary)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--primary)" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {gridYs.map(g => (
          <g key={g.y}>
            <line x1={pad.l} y1={g.y} x2={W - pad.r} y2={g.y}
              stroke="var(--border)" strokeWidth="0.6" strokeDasharray="3 3" />
            <text x={pad.l - 4} y={g.y + 3} textAnchor="end" fontSize="9"
              fill="var(--text-muted)">{g.val}</text>
          </g>
        ))}

        {/* Area fill */}
        <path d={area} fill="url(#aGrad)" />

        {/* Line */}
        <path d={line} fill="none" stroke="var(--primary)" strokeWidth="1.8"
          strokeLinejoin="round" strokeLinecap="round" />

        {/* Data points */}
        {pts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="2.5" fill="var(--primary)"
            opacity={trends[i].count > 0 ? 1 : 0} />
        ))}

        {/* X axis labels */}
        {xLabels.map(({ i, day }) => (
          <text key={i} x={px(i)} y={H - 4} textAnchor="middle" fontSize="9"
            fill="var(--text-muted)">{day.slice(5)}</text>
        ))}
      </svg>
    </div>
  );
}

/* ─────────────── CSV Export ─────────────── */
function exportCSV(summary, trends, days) {
  const rows = [];

  rows.push(['Report Export', `Last ${days} days`, new Date().toLocaleDateString()]);
  rows.push([]);
  rows.push(['SUMMARY']);
  rows.push(['Total Tickets', summary.total_tickets]);
  rows.push(['Resolved', summary.resolved_total]);
  rows.push(['SLA Breached', summary.sla_breached]);
  rows.push(['SLA Breach Rate (%)', summary.sla_breach_rate]);
  rows.push(['Avg Resolution (hrs)', summary.avg_resolution_hours || 'N/A']);
  rows.push([]);

  rows.push(['BY STATUS', 'Count']);
  (summary.by_status || []).forEach(r => rows.push([r.status, r.count]));
  rows.push([]);

  rows.push(['BY PRIORITY', 'Count']);
  (summary.by_priority || []).forEach(r => rows.push([r.priority, r.count]));
  rows.push([]);

  rows.push(['BY TYPE', 'Count']);
  (summary.by_type || []).forEach(r => rows.push([r.ticket_type, r.count]));
  rows.push([]);

  rows.push(['BY SERVICE CATEGORY', 'Count']);
  (summary.by_service_category || []).forEach(r => rows.push([r.service_category, r.count]));
  rows.push([]);

  rows.push(['DAILY TREND', 'Count']);
  trends.forEach(r => rows.push([r.day, r.count]));

  const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `itsm_report_${days}d.csv`;
  a.click(); URL.revokeObjectURL(url);
}

/* ─────────────── Main Page ─────────────── */
export default function ReportsPage() {
  const { user }  = useAuth();
  const isAdmin   = user?.role === 'admin';

  const [days,        setDays]        = useState(30);
  const [summary,     setSummary]     = useState(null);
  const [trends,      setTrends]      = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [loading,     setLoading]     = useState(true);

  const fetchData = useCallback(() => {
    setLoading(true);
    const calls = [getReportSummary({ days }), getReportTrends({ days })];
    if (isAdmin) calls.push(getConsultantStats());
    Promise.all(calls)
      .then(([sRes, tRes, cRes]) => {
        setSummary(sRes.data);
        setTrends(tRes.data.trends || []);
        if (cRes) setConsultants(cRes.data.consultants || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [days, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const trendPeak = trends.length > 0 ? Math.max(...trends.map(t => t.count)) : 0;
  const trendSum  = trends.reduce((s, t) => s + t.count, 0);

  return (
    <AppLayout pageTitle="Reports & Analytics">
      {/* ── Header: date range + export ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{
              padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', border: '1.5px solid',
              borderColor: days === d ? 'var(--primary)' : 'var(--border)',
              background:  days === d ? 'var(--primary-light)' : 'transparent',
              color:       days === d ? 'var(--primary)' : 'var(--text-muted)',
              transition: 'all 0.15s',
            }}>Last {d}d</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => fetchData()} className="btn btn-secondary btn-sm">
            🔄 Refresh
          </button>
          {summary && (
            <button onClick={() => exportCSV(summary, trends, days)}
              className="btn btn-secondary btn-sm"
              style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export CSV
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex-center" style={{ padding: 80 }}>
          <div className="spinner" style={{ width: 36, height: 36 }} />
        </div>
      ) : (
        <>
          {/* ── Summary stat cards ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Total Tickets',    value: summary?.total_tickets      || 0, icon: '🎫', color: 'var(--primary)', sub: `in last ${days} days` },
              { label: 'Resolved',         value: summary?.resolved_total     || 0, icon: '✅', color: '#10b981',        sub: 'closed successfully'  },
              { label: 'SLA Breached',     value: summary?.sla_breached       || 0, icon: '⚠️', color: '#ef4444',        sub: `${summary?.sla_breach_rate || 0}% breach rate` },
              { label: 'Avg Resolution',   value: summary?.avg_resolution_hours ? `${summary.avg_resolution_hours}h` : '—', icon: '⏱️', color: '#f59e0b', sub: 'hours per ticket' },
            ].map(c => (
              <div key={c.label} className="card" style={{ padding: '16px 18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <span style={{ fontSize: 18 }}>{c.icon}</span>
                  <span style={{ fontSize: 26, fontWeight: 800, color: c.color, lineHeight: 1 }}>{c.value}</span>
                </div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)' }}>{c.label}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{c.sub}</div>
              </div>
            ))}
          </div>

          {/* ── Donut charts row ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 16 }}>
            {[
              { title: 'By Status',   data: summary?.by_status,   colorMap: STATUS_COLORS,   labelKey: 'status' },
              { title: 'By Priority', data: summary?.by_priority, colorMap: PRIORITY_COLORS, labelKey: 'priority' },
              { title: 'By Type',     data: summary?.by_type,     colorMap: TYPE_COLORS,     labelKey: 'ticket_type' },
            ].map(c => (
              <div key={c.title} className="card">
                <div style={{ padding: '14px 18px 0', fontWeight: 700, fontSize: 13.5, color: 'var(--text-primary)', marginBottom: 12 }}>
                  {c.title}
                </div>
                <div style={{ padding: '0 16px 18px' }}>
                  <DonutChart
                    data={c.data}
                    colorMap={c.colorMap}
                    labelKey={c.labelKey}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* ── Trend Area Chart ── */}
          <div className="card mb-4">
            <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)' }}>
              <div>
                <span style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text-primary)' }}>
                  Ticket Trend — Last {days} Days
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 10 }}>
                  {trendSum} total · Peak {trendPeak}
                </span>
              </div>
            </div>
            <div style={{ padding: '16px 16px 12px' }}>
              <AreaChart trends={trends} />
            </div>
          </div>

          {/* ── Service Category Breakdown ── */}
          {summary?.by_service_category?.length > 0 && (
            <div className="card mb-4">
              <div style={{ padding: '14px 18px 0', fontWeight: 700, fontSize: 13.5, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)', paddingBottom: 12, marginBottom: 0 }}>
                By Service Category
              </div>
              <div style={{ padding: '16px 20px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(() => {
                  const catData = summary.by_service_category;
                  const maxCount = Math.max(...catData.map(d => parseInt(d.count) || 0), 1);
                  return catData.map((row, i) => {
                    const count = parseInt(row.count) || 0;
                    const pct   = (count / maxCount) * 100;
                    const color = CATEGORY_COLORS[i % CATEGORY_COLORS.length];
                    return (
                      <div key={row.service_category} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ width: 130, fontSize: 12.5, color: 'var(--text-secondary)', fontWeight: 600, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.service_category}
                        </span>
                        <div style={{ flex: 1, height: 20, background: 'var(--bg-hover)', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', width: `${pct}%`, background: color,
                            borderRadius: 4, transition: 'width 0.7s ease',
                            display: 'flex', alignItems: 'center', paddingLeft: 7,
                          }}>
                            {pct > 18 && <span style={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>{count}</span>}
                          </div>
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: color, width: 28, textAlign: 'right', flexShrink: 0 }}>{count}</span>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {/* ── Consultant Workload (admin only) ── */}
          {isAdmin && (
            <div className="card">
              <div style={{ padding: '14px 18px', fontWeight: 700, fontSize: 13.5, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)' }}>
                Consultant Workload
              </div>
              {consultants.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">👥</div>
                  <p>No consultant data available.</p>
                </div>
              ) : (
                <div style={{ padding: '8px 0 4px' }}>
                  {consultants.map((c, i) => {
                    const resolveRate = c.assigned_total > 0
                      ? ((c.resolved_count / c.assigned_total) * 100).toFixed(0)
                      : 0;
                    const maxAssigned = Math.max(...consultants.map(x => x.assigned_total), 1);
                    return (
                      <div key={c.id} style={{
                        display: 'grid', gridTemplateColumns: '180px 1fr 80px 80px 80px 90px',
                        alignItems: 'center', gap: 12,
                        padding: '10px 18px',
                        borderBottom: i < consultants.length - 1 ? '1px solid var(--border-light)' : 'none',
                      }}>
                        {/* Name */}
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{c.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.email}</div>
                        </div>

                        {/* Workload bar */}
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Workload</span>
                            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--primary)' }}>{c.assigned_total}</span>
                          </div>
                          <div style={{ height: 5, background: 'var(--bg-hover)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{
                              height: '100%', borderRadius: 3,
                              width: `${(c.assigned_total / maxAssigned) * 100}%`,
                              background: 'var(--primary)', transition: 'width 0.7s ease',
                            }} />
                          </div>
                        </div>

                        {/* Resolved */}
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Resolved</div>
                          <span style={{
                            fontSize: 13, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                            background: 'rgba(16,185,129,0.10)', color: '#059669',
                          }}>{c.resolved_count}</span>
                        </div>

                        {/* Open */}
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Open</div>
                          <span style={{
                            fontSize: 13, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                            background: 'rgba(245,158,11,0.10)', color: '#d97706',
                          }}>{c.open_count}</span>
                        </div>

                        {/* Resolve rate */}
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Rate</div>
                          <span style={{
                            fontSize: 13, fontWeight: 700,
                            color: resolveRate >= 70 ? '#059669' : resolveRate >= 40 ? '#d97706' : '#dc2626',
                          }}>{resolveRate}%</span>
                        </div>

                        {/* Avg hours */}
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Avg Resolution</div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
                            {c.avg_resolution_hours ? `${c.avg_resolution_hours}h` : '—'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </AppLayout>
  );
}
